"use client";
import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { ApiError } from "@/lib/api";
import {
  createEvaluation,
  type EvaluationMutationResult,
} from "@/lib/api/evaluations";
import type { CommandOptions } from "@/lib/api/suppliers";
import { getOperationByKey } from "@/lib/api/suppliers";
import { createCommandKey } from "@/lib/commands/command-key";
import {
  evaluationCompletion,
  type EvaluationOperation,
} from "@/lib/commands/evaluation-operation-store";
import {
  evaluationFeedback,
  type EvaluationFeedback,
} from "@/lib/commands/evaluation-feedback";
import { eventAuthorityLost } from "@/lib/commands/event-feedback";
import { eventBindingKey } from "@/lib/commands/event-operation-store";
import { invalidateEvent } from "@/lib/commands/event-invalidation";
import type { EventRuntimeConfig } from "@/components/events/event-runtime";
import { useEvaluationRecovery } from "./use-evaluation-recovery";

interface Runtime {
  config: EventRuntimeConfig;
  records: EvaluationOperation[];
  ready: boolean;
  available: boolean;
  pending: boolean;
  lookupFailed: boolean;
  terminal?: "Completed" | "Failed";
  epoch: number;
  denied: (eventId: string) => boolean;
  deny: (eventId: string, error: unknown) => void;
  retry: (eventId: string) => void;
  blocked: (eventId: string) => boolean;
  restore: () => void;
  dismiss: () => void;
  completion: (
    evaluationId: string,
    type: EvaluationOperation["operationType"],
  ) => number;
  run: (
    scope: Omit<EvaluationOperation, "idempotencyKey" | "startedAt">,
    send: (
      options: CommandOptions,
    ) => Promise<{ data: EvaluationMutationResult }>,
  ) => Promise<{
    id?: string;
    result?: EvaluationMutationResult;
    feedback?: EvaluationFeedback;
  }>;
  create: (
    eventId: string,
    revisionId: string,
  ) => Promise<{ id?: string; feedback?: EvaluationFeedback }>;
  lookup: (operation: EvaluationOperation) => Promise<void>;
}
const Context = createContext<Runtime | null>(null);
export function EvaluationRuntimeProvider({
  config,
  children,
}: {
  config: EventRuntimeConfig;
  children: ReactNode;
}) {
  const recovery = useEvaluationRecovery(config.binding);
  const latest = useRef(config),
    controller = useRef<AbortController | null>(null),
    mounted = useRef(false);
  const denied = useRef(new Set<string>());
  const [epoch, setEpoch] = useState(0),
    [pending, setPending] = useState(false);
  const [lookupFailed, setLookupFailed] = useState(false),
    [terminal, setTerminal] = useState<Runtime["terminal"]>();
  const [completions, setCompletions] = useState<Record<string, number>>({});
  useEffect(() => {
    latest.current = config;
  }, [config]);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      controller.current?.abort();
    };
  }, []);
  const alive = (eventId: string, signal: AbortSignal) =>
    mounted.current && !signal.aborted && !denied.current.has(eventId);
  const blocked = (eventId: string) =>
    !recovery.available ||
    pending ||
    denied.current.has(eventId) ||
    recovery.records.some((x) => x.eventId === eventId);
  function deny(eventId: string, error: unknown) {
    controller.current?.abort();
    denied.current.add(eventId);
    setEpoch((x) => x + 1);
    try {
      recovery
        .read()
        .filter((x) => x.eventId === eventId)
        .forEach((x) => recovery.remove(x.idempotencyKey));
    } catch {}
    if (error instanceof ApiError && error.kind === "unauthorized")
      latest.current.invalidate(error);
  }
  function finish(record: EvaluationOperation, recovered: boolean) {
    if (!recovery.remove(record.idempotencyKey)) return false;
    if (recovered) {
      setTerminal("Completed");
      if (record.evaluationId)
        setCompletions((before) => {
          const key = `${record.evaluationId}:${record.operationType}`;
          return { ...before, [key]: (before[key] ?? 0) + 1 };
        });
    }
    invalidateEvent(record.eventId);
    return true;
  }
  async function perform(
    record: EvaluationOperation,
    signal: AbortSignal,
    send: (
      options: CommandOptions,
    ) => Promise<{ data: EvaluationMutationResult }>,
  ) {
    try {
      if (recovery.read().some((x) => x.eventId === record.eventId))
        return { feedback: "blocked" as const };
      if (!(await latest.current.verify(signal)))
        return { feedback: "forbidden" as const };
      if (!alive(record.eventId, signal))
        return { feedback: "unknown" as const };
      if (!recovery.save(record)) return { feedback: "storage" as const };
      const response = await send({
        tenantId: config.binding.tenantId,
        csrfToken: latest.current.csrfToken,
        idempotencyKey: record.idempotencyKey,
        signal,
      });
      const id = evaluationCompletion(response.data, record);
      if (!alive(record.eventId, signal) || !id)
        return { feedback: "unknown" as const };
      return finish(record, false)
        ? { id, result: response.data }
        : { feedback: "storage" as const };
    } catch (error) {
      if (!alive(record.eventId, signal))
        return { feedback: "unknown" as const };
      const feedback = evaluationFeedback(error);
      if (feedback !== "unknown") recovery.remove(record.idempotencyKey);
      if (eventAuthorityLost(error)) deny(record.eventId, error);
      else invalidateEvent(record.eventId);
      return { feedback };
    }
  }
  async function run(
    scope: Omit<EvaluationOperation, "idempotencyKey" | "startedAt">,
    send: (
      options: CommandOptions,
    ) => Promise<{ data: EvaluationMutationResult }>,
  ) {
    const { eventId } = scope;
    if (controller.current || blocked(eventId))
      return { feedback: "blocked" as const };
    if (!navigator.locks) return { feedback: "storage" as const };
    const abort = new AbortController();
    controller.current = abort;
    setPending(true);
    const record: EvaluationOperation = {
      ...scope,
      idempotencyKey: createCommandKey("evaluation"),
      startedAt: Date.now(),
    };
    try {
      return await navigator.locks.request(
        eventBindingKey(config.binding),
        { ifAvailable: true },
        (lock) =>
          lock
            ? perform(record, abort.signal, send)
            : Promise.resolve({ feedback: "blocked" as const }),
      );
    } catch {
      return { feedback: "storage" as const };
    } finally {
      if (controller.current === abort) controller.current = null;
      if (mounted.current) setPending(false);
    }
  }
  function create(eventId: string, revisionId: string) {
    return run(
      { eventId, revisionId, operationType: "evaluation.create" },
      (options) =>
        createEvaluation(config.binding, eventId, revisionId, options),
    );
  }
  async function lookup(record: EvaluationOperation) {
    if (controller.current || denied.current.has(record.eventId)) return;
    const abort = new AbortController();
    controller.current = abort;
    setPending(true);
    setLookupFailed(false);
    try {
      if (!(await latest.current.verify(abort.signal))) return;
      const response = await getOperationByKey(
        { tenantId: config.binding.tenantId, signal: abort.signal },
        record.operationType,
        record.idempotencyKey,
      );
      if (!alive(record.eventId, abort.signal)) return;
      if (response.data.state === "Failed") {
        if (recovery.remove(record.idempotencyKey)) setTerminal("Failed");
        return;
      }
      if (
        response.data.state !== "Completed" ||
        !evaluationCompletion(response.data.result, record)
      ) {
        setLookupFailed(true);
        return;
      }
      finish(record, true);
    } catch (error) {
      if (!alive(record.eventId, abort.signal)) return;
      if (eventAuthorityLost(error)) deny(record.eventId, error);
      else setLookupFailed(true);
    } finally {
      if (controller.current === abort) controller.current = null;
      if (mounted.current) setPending(false);
    }
  }
  return (
    <Context.Provider
      value={{
        config,
        records: recovery.records,
        ready: recovery.ready,
        available: recovery.available,
        pending,
        lookupFailed,
        terminal,
        epoch,
        denied: (id) => denied.current.has(id),
        deny,
        retry: (id) => {
          denied.current.delete(id);
          setEpoch((x) => x + 1);
        },
        blocked,
        restore: recovery.sync,
        dismiss: () => setTerminal(undefined),
        completion: (id, type) => completions[`${id}:${type}`] ?? 0,
        run,
        create,
        lookup,
      }}
    >
      {children}
    </Context.Provider>
  );
}
export function useEvaluationRuntime() {
  const value = useContext(Context);
  if (!value) throw new Error("Evaluation runtime is unavailable");
  return value;
}
